import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { cx } from '../lib/cx';

export interface ResizeHandleProps {
  /** Announced to assistive tech: "Resize thread list". */
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  /** Live, while dragging. */
  readonly onChange: (value: number) => void;
  /** Once, where the drag (or key press) ended — the value worth saving. */
  readonly onCommit: (value: number) => void;
  /** Double-click: back to the default. */
  readonly onReset: () => void;
  /** Horizontal pixels dragged, in the units of `value`. */
  readonly pxToValue?: (px: number) => number;
  /** How far one arrow-key press moves it, in the units of `value`. */
  readonly step: number;
  readonly className?: string;
}

/**
 * The divider between two side-by-side panes: drag it, arrow-key it once
 * focused, double-click it to put it back.
 *
 * A focusable `separator` with a value, which is what makes a splitter
 * operable without a mouse — the pattern WAI-ARIA describes as a "window
 * splitter". The line is drawn thin and the grab area is wide, so it is easy
 * to catch without looking like a bar.
 */
export function ResizeHandle({
  label,
  value,
  min,
  max,
  onChange,
  onCommit,
  onReset,
  pxToValue = (px) => px,
  step,
  className,
}: ResizeHandleProps) {
  const drag = useRef<{ startX: number; startValue: number; latest: number } | null>(null);
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { startX: event.clientX, startValue: value, latest: value };
    // Without these the drag selects text across both panes and the cursor
    // flickers to an I-beam whenever it outruns the handle.
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current === null) return;
    const next = clamp(drag.current.startValue + pxToValue(event.clientX - drag.current.startX));
    drag.current.latest = next;
    onChange(next);
  };

  const endDrag = () => {
    if (drag.current === null) return;
    onCommit(drag.current.latest);
    drag.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onReset}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const next = clamp(value + (event.key === 'ArrowLeft' ? -step : step));
        onChange(next);
        onCommit(next);
      }}
      className={cx(
        'group relative flex-none cursor-col-resize touch-none outline-none select-none',
        className,
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-line transition-[width,background-color] group-hover:w-[3px] group-hover:bg-accent group-focus-visible:w-[3px] group-focus-visible:bg-accent group-active:w-[3px] group-active:bg-accent"
      />
    </div>
  );
}
